// Streaming client for the AI Concierge (Aria). Mirrors the SDK's PatientClient.conciergeStream
// exactly, so this page can ship before the SDK is published — swap to api.conciergeStream
// once the new @forcebase-ai/api-client version lands. Auth matches every other patient call: the
// Cognito bearer (cc-patient) in the Authorization header when present, plus credentials:include for
// the dev-session cookie fallback. (A bare credentials:include is NOT enough under Cognito — the token
// is a bearer, not a cookie — which is why the concierge 403'd with wrong_audience without this.)
import { API_ORIGIN } from "../config";

export type ConciergeRole = "user" | "assistant";
export interface ConciergeMessage {
  role: ConciergeRole;
  content: string;
}
/** chunk → append; refusal → discard the partial and show this; error → show this and stop. */
export type ConciergeEvent =
  | { type: "chunk"; text: string }
  | { type: "refusal"; text: string }
  | { type: "error"; text: string };

/**
 * Neutralize "Zalgo"/abusive combining-mark text before it reaches the concierge. Zalgo stacks dozens
 * of combining marks per character, so a short visible message balloons past the API's 4000-char cap
 * — the request 400s and (because the message stays in the transcript) every later send re-fails until
 * a full reload. We NFC-normalize, cap stacked combining marks at 2 per cluster (legit accents keep
 * their single mark; zalgo's excess is dropped), strip zero-width chars, and trim. Returns "" if the
 * input was only junk. Mirror of the server-side guard in the API's concierge route.
 */
/** A persisted transcript message (server-side history), carrying its stored timestamp. */
export type ConciergeHistoryMessage = ConciergeMessage & { ts: string };

/** Load the member's concierge history (past 12 months, oldest→newest). Same auth as the stream:
 * Cognito bearer when present, plus credentials:include for the dev-session fallback. Returns [] on any
 * failure so the page still opens to a fresh chat. */
export async function fetchConciergeHistory(
  getToken: () => Promise<string | null>,
): Promise<ConciergeHistoryMessage[]> {
  const token = await getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(`${API_ORIGIN}/v1/patient/concierge/history`, {
      headers,
      credentials: "include",
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { data?: { messages?: ConciergeHistoryMessage[] } };
    return body?.data?.messages ?? [];
  } catch {
    return [];
  }
}

export function sanitizeConciergeText(raw: string): string {
  return raw
    .slice(0, 50_000)
    .normalize("NFC")
    .replace(/(\p{M}{1,2})\p{M}+/gu, "$1")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
}

export async function* conciergeStream(
  messages: ConciergeMessage[],
  getToken: () => Promise<string | null>,
): AsyncGenerator<ConciergeEvent> {
  // Send the browser's IANA timezone so Aria states visit times in the member's zone — matching what
  // the Visits page renders (which formats in browser-local time). Without it the server formats UTC.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const token = await getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_ORIGIN}/v1/patient/concierge`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({ messages, tz }),
  });

  if (!res.ok || !res.body) {
    let text = "Sorry — I couldn't reach the concierge just now.";
    if (res.status === 429) text = "You're sending messages a little fast — give me a moment.";
    else {
      try {
        text = (await res.json())?.error?.message ?? text;
      } catch {
        /* non-JSON */
      }
    }
    yield { type: "error", text };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (event === "done") return;
      let text = "";
      try {
        text = (JSON.parse(data) as { text?: string }).text ?? "";
      } catch {
        /* ignore malformed frame */
      }
      if (event === "chunk") yield { type: "chunk", text };
      else if (event === "refusal") yield { type: "refusal", text };
      else if (event === "error") yield { type: "error", text };
    }
  }
}
